/**
 * Shared "export mode" flag for chart rendering.
 *
 * ECharts HTML charts (backend-rendered, embedded via `srcDoc` in a sandboxed
 * iframe) cannot be captured by html2canvas — the iframe is a separate
 * browsing context, so html2canvas's DOM clone never sees its content
 * (cross-origin/sandbox restrictions), which is why PDF/DOCX/PPT export
 * showed a blank rectangle where the chart should be.
 *
 * The fix: while exporting, temporarily render the equivalent Recharts-based
 * chart component (already implemented as the html2canvas-capturable
 * fallback for every chart type) INSTEAD of the ECharts iframe, export, then
 * switch back. This must happen in the *live* React tree — html2canvas's
 * `onclone` callback only gets a cloned snapshot of already-rendered DOM, it
 * cannot trigger a React re-render — so the toggle has to be flipped before
 * html2canvas is invoked, with a render committed in between.
 *
 * `useSyncExternalStore` (React 18+) lets any number of chart components
 * subscribe and re-render synchronously with the rest of the tree when the
 * flag changes, without threading a prop through every intermediate layer.
 */

type Listener = () => void;

let exportModeActive = false;
const listeners = new Set<Listener>();

function emitChange(): void {
    listeners.forEach((listener) => listener());
}

export function subscribeChartExportMode(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function getChartExportModeSnapshot(): boolean {
    return exportModeActive;
}

function setChartExportMode(active: boolean): void {
    if (exportModeActive === active) return;
    exportModeActive = active;
    emitChange();
}

/**
 * Turns export mode on, waits for React to commit + paint the resulting
 * re-render (Recharts fallbacks mounting in place of ECharts iframes), runs
 * `run`, then always turns export mode back off — even if `run` throws.
 */
export async function withChartExportMode<T>(run: () => Promise<T>): Promise<T> {
    setChartExportMode(true);
    try {
        // Two rAF ticks: one for React to flush the state update into the DOM,
        // one for the browser to paint it — Recharts (SVG) needs a real layout
        // pass before html2canvas captures accurate dimensions.
        await new Promise<void>((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        });
        return await run();
    } finally {
        setChartExportMode(false);
    }
}
